import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { DayOfWeek, Language, MealType, Recipe } from '../types';
import { buildCookingSessionDocId } from '../utils/cookingSessionKeys';
import {
    getRecipeStepKeys,
    groupRecipeInstructions,
    isPlannedRecipeCookingDone,
    isRecipeStepsComplete,
} from '../utils/cookingSessionSteps';
import { getTranslation } from '../utils/translations';

interface UseCookingSessionDocumentOptions {
    activeHouseholdId?: string;
    weekOffset: number;
    day: DayOfWeek;
    mealType: MealType;
    defaultServingsByRecipeId: Record<string, number>;
    language?: Language;
}

export function useCookingSessionDocument({
    activeHouseholdId,
    weekOffset,
    day,
    mealType,
    defaultServingsByRecipeId,
    language,
}: UseCookingSessionDocumentOptions) {
    const [struckIngredients, setStruckIngredients] = useState<Set<string>>(() => new Set());
    const [completedSteps, setCompletedSteps] = useState<Set<string>>(() => new Set());
    const [completedRecipes, setCompletedRecipes] = useState<Set<string>>(() => new Set());
    const [servingsMap, setServingsMap] = useState<Record<string, number>>(() => ({ ...defaultServingsByRecipeId }));

    const sessionKey = useMemo(
        () => buildCookingSessionDocId(weekOffset, day, mealType),
        [weekOffset, day, mealType]
    );

    const sessionDocRef = useMemo(() => {
        if (!activeHouseholdId) return null;
        return doc(db, 'households', activeHouseholdId, 'cookingSessions', sessionKey);
    }, [activeHouseholdId, sessionKey]);

    const defaultServingsKey = useMemo(
        () => Object.entries(defaultServingsByRecipeId).sort(([a], [b]) => a.localeCompare(b)).map(([id, servings]) => `${id}:${servings}`).join(','),
        [defaultServingsByRecipeId]
    );

    useEffect(() => {
        if (!sessionDocRef) return;

        const unsubscribe = onSnapshot(sessionDocRef, async (snap) => {
            if (snap.exists()) {
                const data = snap.data();
                setStruckIngredients(new Set(Array.isArray(data.struckIngredients) ? data.struckIngredients : []));
                setCompletedSteps(new Set(Array.isArray(data.completedSteps) ? data.completedSteps : []));
                setCompletedRecipes(new Set(Array.isArray(data.completedRecipes) ? data.completedRecipes : []));
                const dbServings = (data.servingsMap && typeof data.servingsMap === 'object')
                    ? data.servingsMap as Record<string, number>
                    : {};
                const mergedServings = { ...defaultServingsByRecipeId, ...dbServings };
                setServingsMap(mergedServings);

                const needsUpdate = Object.keys(defaultServingsByRecipeId).some((recipeId) => !(recipeId in dbServings));
                if (needsUpdate) {
                    await setDoc(sessionDocRef, {
                        servingsMap: mergedServings,
                        updatedAt: serverTimestamp(),
                    }, { merge: true });
                }
                return;
            }

            await setDoc(sessionDocRef, {
                struckIngredients: [],
                preppedIngredients: [],
                completedSteps: [],
                completedRecipes: [],
                mealCompleted: false,
                servingsMap: { ...defaultServingsByRecipeId },
                updatedAt: serverTimestamp(),
            });
        }, (error) => {
            console.error('Cooking session document listener error:', error);
        });

        return () => unsubscribe();
    }, [sessionDocRef, defaultServingsKey, defaultServingsByRecipeId]);

    const toggleStrike = async (key: string) => {
        const next = new Set(struckIngredients);
        if (next.has(key)) next.delete(key);
        else next.add(key);

        if (sessionDocRef) {
            await updateDoc(sessionDocRef, { struckIngredients: Array.from(next) });
        } else {
            setStruckIngredients(next);
        }
    };

    const toggleStepComplete = async (key: string) => {
        const next = new Set(completedSteps);
        if (next.has(key)) next.delete(key);
        else next.add(key);

        if (sessionDocRef) {
            await updateDoc(sessionDocRef, { completedSteps: Array.from(next) });
        } else {
            setCompletedSteps(next);
        }
    };

    const updateRecipeServings = async (recipeId: string, newCount: number) => {
        const nextCount = Math.max(0.5, newCount);
        const nextServings = { ...servingsMap, [recipeId]: nextCount };

        if (sessionDocRef) {
            await updateDoc(sessionDocRef, { servingsMap: nextServings });
        } else {
            setServingsMap(nextServings);
        }
    };

    const markRecipeDone = async (recipeId: string, recipe: Recipe) => {
        const prepTabLabel = getTranslation(language, 'prepTab');
        const sections = groupRecipeInstructions(recipe, prepTabLabel);
        const stepKeys = getRecipeStepKeys(recipeId, sections);
        const nextSteps = new Set(completedSteps);
        stepKeys.forEach((key) => nextSteps.add(key));
        const nextRecipes = new Set(completedRecipes);
        nextRecipes.add(recipeId);

        if (sessionDocRef) {
            await updateDoc(sessionDocRef, {
                completedSteps: Array.from(nextSteps),
                completedRecipes: Array.from(nextRecipes),
            });
        } else {
            setCompletedSteps(nextSteps);
            setCompletedRecipes(nextRecipes);
        }
    };

    const isRecipeDone = (recipeId: string, recipe: Recipe) => {
        const prepTabLabel = getTranslation(language, 'prepTab');
        return isPlannedRecipeCookingDone(recipeId, recipe, {
            completedRecipes,
            completedSteps,
        }, prepTabLabel);
    };

    return {
        sessionKey,
        struckIngredients,
        completedSteps,
        completedRecipes,
        servingsMap,
        toggleStrike,
        toggleStepComplete,
        updateRecipeServings,
        markRecipeDone,
        isRecipeDone,
        isRecipeStepsComplete: (recipeId: string, recipe: Recipe) => {
            const prepTabLabel = getTranslation(language, 'prepTab');
            const sections = groupRecipeInstructions(recipe, prepTabLabel);
            return isRecipeStepsComplete(recipeId, sections, completedSteps);
        },
    };
}
